import { useCallback } from 'react';

import { useToast } from '@ecoinvent/ui';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import api from 'api/api';
import useQueryErrorEffect from 'hooks/useQueryErrorEffect';
import useVersionInfo from 'hooks/useVersionInfo';
import { isV4Release } from 'utilities/constants/versions';

import { FetchFlowDetailsParams } from '../../types';

import { FlowDetails, FlowsType } from './types';

const fetchFlowDetails = async (params: FetchFlowDetailsParams) => {
  const { data } = await api.get(`/web/v4/uprs/${params.dataset_id}/flows/${params.flow_type}/${params.flow_id}`, { params });

  return data;
};

/** Only v4 releases serve this endpoint, so the query stays idle on 3.x. See useFlows. */
const useFlowDetails = (uprId: string, flowType: FlowsType, flowId?: string) => {
  const toast = useToast();

  const { ver: version, system_model } = useVersionInfo();
  const queryKey = ['flowDetail', uprId, flowId, version, system_model];
  const query = useQuery<FlowDetails, AxiosError>({
    queryKey,
    queryFn: () =>
      fetchFlowDetails({
        dataset_id: uprId,
        flow_type: flowType,
        flow_id: flowId as string,
        version,
        system_model,
      }),
    enabled: isV4Release(version) && !!uprId && !!flowId,
    retry: false,
  });

  const showError = useCallback(() => {
    toast({
      title: 'Oh no...',
      description: 'There was an error opening this flow. Please try again later.',
      status: 'error',
      isClosable: true,
    });
  }, [toast]);
  useQueryErrorEffect(query, queryKey, showError);

  return query;
};

export default useFlowDetails;
